<script lang="ts" setup>
import { ElMessage } from "element-plus";
import { ref, onUnmounted, computed, watch, reactive } from "vue";
import router from "../../router";
import { useAuthStore } from "../../store/authStore";
import axios from "axios";
import { DateTime } from "luxon";
import { filterCustomerDeliveryLogs, customerActivityLabel } from "../../utils/customerDeliveryLogs";
import {
  type StopDetailView,
  stopDetailsByIndex,
  hasVisibleStopDetail,
  formatPhone,
  telHref,
  orderTypeLabel,
  pickupLabel,
  loadersLabel,
  preferredDriverNote,
} from "../../utils/orderExtraInfo";
import { formatBookedFor } from "../../utils/orderTime";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

declare const google: any;
const loading = ref(true);
const orderObject = reactive({ data: <any>null });
const myMapRef = ref<any>(null);
const mapCenter = { lat: -1.23266, lng: 36.77494 };
const routePath = ref<{ lat: number; lng: number }[]>([]);

const isValidTrackOrderData = (d: any): boolean => {
  if (!d || typeof d !== "object") return false;
  const od = d.order_details;
  if (!od || typeof od !== "object") return false;
  if (od.order_number == null || od.order_status_text == null || od.order_status == null) return false;
  if (!Array.isArray(d.paths)) return false;
  const pd = d.payment_details;
  if (!pd || typeof pd !== "object" || pd.customer_amount == null) return false;
  if (!Array.isArray(d.delivery_logs)) return false;
  return true;
};

const orderDetailsLoaded = computed(() => orderObject.data !== null);

const droppedDeliveryLog = computed(() => {
  if (!orderObject.data) return null;
  return orderObject.data.delivery_logs.find((log: any) => log.log_type === "Dropped") ?? null;
});

const customerDeliveryLogs = computed(() =>
  filterCustomerDeliveryLogs(orderObject.data?.delivery_logs),
);

// `lookup` is either { token: '...' } for new unguessable tracking links, or
// { order: '...' } for legacy shared links from before tracking_token existed.
// Token branch hits the public /track endpoint (used by other internal apps);
// order-number branch hits the authenticated /orders endpoint (company-scoped).
// `isPoll` is true for the 30s background refresh — a transient failure then
// must keep the last good data on screen and stay quiet (no error toast).
// The current JWT, read at call time: the axios default set when this module
// loaded may predate a sign-in.
const authHeader = () => {
  const jwt = store.getToken;
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
};

const getOrderDetails = async (lookup: { token?: string; order?: string }, isPoll = false) => {
  const fail = (message: string) => {
    if (isPoll) { console.log("track refresh failed:", message); return; }
    orderObject.data = null;
    ElMessage({ message, type: "error" });
  };
  try {
    const base = import.meta.env.VITE_ORDERS_SERVICE;
    // The signed-in lookup sends the JWT as a header, never in the URL (it
    // would land in access logs and browser history).
    const response = lookup.token
      ? await axios.get(`${base}track?token=${encodeURIComponent(lookup.token)}`)
      : await axios.get(`${base}orders?order_number=${encodeURIComponent(lookup.order || '')}`, { headers: authHeader() });
    if (response.status == 200 && response.data.status) {
      const d = response.data.data;
      if (isValidTrackOrderData(d)) {
        orderObject.data = d;
        // The signed-in /orders answer already carries the stop contacts;
        // /track never does, so ask for them separately.
        if (lookup.order) takeStopDetails(d.order_details.order_number, d.stop_details);
        else void loadStopDetails(String(d.order_details.order_number));
      } else {
        fail(response.data.message || "Unable to load order details for this order.");
      }
    } else {
      fail(response.data.message);
    }
  } catch (er) {
    console.log(er);
    fail("Something went wrong");
  } finally {
    loading.value = false;
  }
};

// ─── Stop contacts ──────────────────────────────────────────────────────
// Receiver name, phone, reference and instructions per stop. They come only
// from the signed-in, company-scoped GET /orders?order_number, never from
// /track (public by token) or /trip. Fetched once per order: they do not
// change while tracking, and a refusal (another company's link) is not
// retried every 30 s.
const stopDetails = ref<Map<number, StopDetailView>>(new Map());
let stopDetailsFor: string | null = null;

const takeStopDetails = (orderNumber: string, list: unknown) => {
  stopDetailsFor = orderNumber;
  stopDetails.value = stopDetailsByIndex(list);
};

const loadStopDetails = async (orderNumber: string) => {
  if (!orderNumber || stopDetailsFor === orderNumber) return;
  stopDetailsFor = orderNumber;
  try {
    // No token in the URL (it would land in access logs and history).
    const r = await axios.get(
      `${import.meta.env.VITE_ORDERS_SERVICE}orders?order_number=${encodeURIComponent(orderNumber)}`,
      { headers: authHeader() },
    );
    if (r.status == 200 && r.data?.status) stopDetails.value = stopDetailsByIndex(r.data.data?.stop_details);
  } catch {
    // Optional extra: the tracking page works without it.
  }
};

const stopDetailAt = (index: number | string): StopDetailView | null => {
  const d = stopDetails.value.get(Number(index));
  return hasVisibleStopDetail(d) ? d! : null;
};

// Order-level extras on order_details; empty for orders placed before them.
const extraInfoRows = computed(() => {
  const od = orderObject.data?.order_details;
  if (!od) return [];
  const rows: { label: string; value: string }[] = [];
  const type = orderTypeLabel(od.order_type);
  if (type) rows.push({ label: "Type", value: type });
  const pickup = pickupLabel(od.pickup_mode, od.scheduled_time);
  if (pickup) rows.push({ label: "Pickup", value: pickup });
  const goods = typeof od.goods_description === "string" ? od.goods_description.trim() : "";
  if (goods) rows.push({ label: "Goods", value: goods });
  const loaders = loadersLabel(od.loaders_count);
  if (loaders) rows.push({ label: "Loaders", value: loaders });
  return rows;
});

// The driver the customer asked for, while the order still waits for one.
// The note ends with the 25 s window, which can close between two polls, so
// a timer set to until_utc re-evaluates it then.
const preferredNow = ref(Date.now());
let preferredTimer: ReturnType<typeof setTimeout> | null = null;
const preferredNote = computed(() => {
  const od = orderObject.data?.order_details;
  // order_status 1 = pending: no driver has accepted yet.
  const pending = Number(od?.order_status) === 1 && Number(orderObject.data?.status ?? 1) === 1;
  // Dedicated is never offered or sent to nearby drivers; its own line
  // (dedicatedDriverNote) says Xobo is assigning.
  if (od?.order_type === "dedicated") return "";
  return preferredDriverNote(od?.preferred, pending, preferredNow.value);
});
watch(
  () => orderObject.data?.order_details?.preferred?.until_utc,
  (untilUtc) => {
    if (preferredTimer) clearTimeout(preferredTimer);
    preferredTimer = null;
    preferredNow.value = Date.now();
    const wait = Date.parse(String(untilUtc ?? "")) - Date.now();
    if (Number.isFinite(wait) && wait > 0) {
      preferredTimer = setTimeout(() => { preferredNow.value = Date.now(); }, wait + 50);
    }
  },
  { immediate: true },
);
onUnmounted(() => { if (preferredTimer) clearTimeout(preferredTimer); });

const awaitingDedicatedDriver = computed(() => {
  const od = orderObject.data?.order_details;
  return od?.order_type === "dedicated" && Number(od?.order_status) === 1 && Number(orderObject.data?.status ?? 1) === 1;
});
// "Xobo is assigning your driver for Tue 4 Nov, 08:00."
const dedicatedDriverNote = computed(() => {
  const at = formatBookedFor(orderObject.data?.order_details?.scheduled_time);
  return at ? `Xobo is assigning your driver for ${at}.` : "Xobo is assigning your driver.";
});

const fetchDirectionsRoute = async (paths: any[]) => {
  if (paths.length < 2) { routePath.value = []; return; }
  try {
    const directionsService = new google.maps.DirectionsService();
    const waypoints = paths.slice(1, -1).map((p: any) => ({
      location: { lat: p.lat, lng: p.lng },
      stopover: true,
    }));
    const result = await directionsService.route({
      origin: { lat: paths[0].lat, lng: paths[0].lng },
      destination: { lat: paths[paths.length - 1].lat, lng: paths[paths.length - 1].lng },
      waypoints,
      travelMode: google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
    });
    const path: { lat: number; lng: number }[] = [];
    result.routes[0].legs.forEach((leg: any) => {
      leg.steps.forEach((step: any) => {
        step.path.forEach((pt: any) => {
          path.push({ lat: pt.lat(), lng: pt.lng() });
        });
      });
    });
    routePath.value = path;
  } catch (e) {
    routePath.value = paths.map((p: any) => ({ lat: p.lat, lng: p.lng }));
  }
};

const updateBounds = () => {
  const mapInstance = myMapRef.value;
  try {
    if (mapInstance && orderObject.data) {
      const paths = orderObject.data.paths;
      if (!paths?.length) return;
      mapInstance.$mapPromise.then((map: any) => {
        const bounds = new google.maps.LatLngBounds();
        paths.forEach((p: any) => bounds.extend(p));
        map.fitBounds(bounds, { left: 460, top: 40, right: 40, bottom: 40 });
      });
    }
  } catch (er) {
    console.log(er);
  }
};

watch(myMapRef, (googleMap) => {
  if (googleMap) {
    googleMap.$mapPromise.then(() => updateBounds());
  }
});

watch(() => orderObject.data, (val) => {
  if (val?.paths?.length) {
    fetchDirectionsRoute(val.paths);
    updateBounds();
  } else {
    routePath.value = [];
  }
});

// --- Auto-refresh: re-fetch order data every 30s while the order is live ---
const POLL_MS = 30000;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let activeLookup: { token?: string; order?: string } | null = null;

const isTerminalStatus = (): boolean => {
  // Under hold-until-approve, order_status=4 is NOT terminal — docs can
  // still be rejected / re-uploaded / approved, which flips the customer-
  // visible label between "Pending approval", "Docs rejected", "Delivered",
  // and "Closed". Compose the same view the badge uses so polling stops at
  // exactly the same moment the label commits to a final state.
  const od = orderObject.data?.order_details;
  if (!od) return false;
  const view = orderStatusView({
    status: od.status,
    order_status: od.order_status,
    verification_status: od.verification_status,
    order_status_text: od.order_status_text,
  });
  return view.kind === "cancelled" || view.kind === "delivered" || view.kind === "closed";
};

const stopPolling = () => {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
};

const startPolling = () => {
  if (pollTimer || !activeLookup) return;
  pollTimer = setInterval(() => {
    if (isTerminalStatus()) { stopPolling(); return; }
    if (activeLookup) getOrderDetails(activeLookup, true);
  }, POLL_MS);
};

onUnmounted(stopPolling);

const tokenQuery = router.currentRoute.value.query.token;
const orderQuery = router.currentRoute.value.query.order;
if (tokenQuery != null && String(tokenQuery).trim() !== "") {
  activeLookup = { token: String(tokenQuery) };
} else if (orderQuery != null && String(orderQuery).trim() !== "") {
  activeLookup = { order: String(orderQuery) };
}
if (activeLookup) {
  getOrderDetails(activeLookup);
  startPolling();
} else {
  loading.value = false;
  ElMessage({ message: "Order number is required", type: "error" });
}

// Render backend timestamps (ISO or SQL "yyyy-MM-dd HH:mm:ss") as a
// human-readable string, e.g. "21 May 2026, 10:38".
const transformDate = (date: string) => {
  if (!date) return "";
  let dt = DateTime.fromISO(date);
  if (!dt.isValid) dt = DateTime.fromSQL(date);
  return dt.isValid ? dt.toFormat("dd MMM yyyy, HH:mm") : String(date);
};

// Prefer the tracking_token from the loaded order; fall back to the token in
// the URL (for orders whose cached Redis blob predates the order_details
// tracking_token field).
const shareableToken = computed(() => {
  const fromOrder = orderObject.data?.order_details?.tracking_token;
  if (fromOrder) return String(fromOrder);
  const fromUrl = router.currentRoute.value.query.token;
  return fromUrl ? String(fromUrl) : null;
});

const shareUrl = computed(() => {
  const t = shareableToken.value;
  return t ? `${window.location.origin}/trip?token=${t}` : "";
});

const shareMessage = computed(() => {
  const orderNo = orderObject.data?.order_details?.order_number;
  return orderNo
    ? `Track your delivery ${orderNo} in real time: ${shareUrl.value}`
    : `Track your delivery in real time: ${shareUrl.value}`;
});

const whatsappShareHref = computed(() => `https://wa.me/?text=${encodeURIComponent(shareMessage.value)}`);
const emailShareHref = computed(() => `mailto:?subject=${encodeURIComponent("Track your delivery")}&body=${encodeURIComponent(shareMessage.value)}`);
const smsShareHref = computed(() => `sms:?body=${encodeURIComponent(shareMessage.value)}`);

const shareMenuOpen = ref(false);
const closeShareMenu = () => { shareMenuOpen.value = false; };

const copyShareUrl = async () => {
  const url = shareUrl.value;
  if (!url) return;
  let ok = false;
  try {
    await navigator.clipboard.writeText(url);
    ok = true;
  } catch {
    // Fallback for browsers that block clipboard API on non-HTTPS or in iframes.
    const el = document.createElement("textarea");
    el.value = url;
    el.setAttribute("readonly", "");
    el.style.position = "absolute";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    el.select();
    try { ok = document.execCommand("copy"); } catch { ok = false; }
    document.body.removeChild(el);
  }
  ElMessage({
    message: ok ? "Tracking link copied" : "Could not copy — long-press the link to copy",
    type: ok ? "success" : "warning",
  });
  closeShareMenu();
};

const shareTrackingLink = async () => {
  if (!shareUrl.value) {
    ElMessage({ message: "No shareable link available for this order", type: "warning" });
    return;
  }
  // Prefer the OS share sheet on mobile / modern desktop browsers.
  const nav: any = typeof navigator !== "undefined" ? navigator : null;
  if (nav && typeof nav.share === "function") {
    try {
      await nav.share({
        title: "Track your delivery",
        text: shareMessage.value,
        url: shareUrl.value,
      });
      return;
    } catch (e: any) {
      // User cancelled (AbortError) — don't pop the fallback menu in that case.
      if (e && e.name === "AbortError") return;
    }
  }
  // No native share or it failed for non-abort reasons — open the fallback menu.
  shareMenuOpen.value = true;
};

import { orderStatusView } from '../../utils/orderStatus';

// Composed status label from order_status + verification_status. A
// dropped-but-unverified order used to say "Delivered" here; now it
// says "Pending approval" until Xobo Ops signs off on the delivery
// docs. The colour swatch is derived from the same view.tone so the
// hero pill matches the OrderHistory row badge for the same order.
const statusView = computed(() => orderStatusView({
  status:              orderObject.data?.status,
  order_status:        orderObject.data?.order_details?.order_status,
  verification_status: orderObject.data?.order_details?.verification_status,
  order_status_text:   orderObject.data?.order_details?.order_status_text,
}));

const statusColor = computed(() => {
  switch (statusView.value.tone) {
    case 'pending':   return { bg: 'rgba(220,38,38,0.10)',  text: '#dc2626', dot: '#dc2626' };
    case 'active':    return { bg: 'rgba(37,99,235,0.10)',  text: '#2563eb', dot: '#2563eb' };
    case 'warning':   return { bg: 'rgba(245,158,11,0.15)', text: '#d97706', dot: '#f59e0b' };
    case 'danger':    return { bg: 'rgba(180,83,9,0.12)',   text: '#b45309', dot: '#f59e0b' };
    case 'delivered': return { bg: 'rgba(34,197,94,0.12)',  text: '#16a34a', dot: '#22c55e' };
    case 'cancelled': return { bg: 'rgba(109,40,217,0.10)', text: '#6d28d9', dot: '#8b5cf6' };
    default:          return { bg: 'rgba(156,163,175,0.15)', text: '#6b7280', dot: '#9ca3af' };
  }
});

const MARKER_COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const getMarkerIcon = (index: number) => {
  const color = MARKER_COLORS[index % MARKER_COLORS.length];
  const label = String.fromCharCode(65 + index);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
      <filter id="shd${index}" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.25)"/>
      </filter>
      <ellipse cx="18" cy="42" rx="6" ry="2.5" fill="rgba(0,0,0,0.12)"/>
      <path d="M18 2C10.268 2 4 8.268 4 16c0 10 14 26 14 26s14-16 14-26C32 8.268 25.732 2 18 2z" fill="${color}" filter="url(#shd${index})"/>
      <circle cx="18" cy="16" r="8" fill="white" opacity="0.92"/>
      <text x="18" y="20" text-anchor="middle" font-size="9" font-weight="700" font-family="DM Sans, sans-serif" fill="${color}">${label}</text>
    </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: 36, height: 44 },
    anchor: { x: 18, y: 44 },
  };
};

const polylineOptions = {
  strokeColor: "#2563eb",
  strokeOpacity: 1.0,
  strokeWeight: 5,
  geodesic: true,
};

const getLocationDisplayText = (location: any): string => {
  // name already includes formatted_address details (combined when setting place)
  // So we just need to return name from backend
  return location.name || "";
};

const mapOptions = {
  styles: [
    { elementType: "geometry", stylers: [{ color: "#eaf0fb" }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#3d4a5c" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
    { featureType: "administrative", elementType: "geometry", stylers: [{ visibility: "off" }] },
    { featureType: "administrative.land_parcel", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#c8e6c9" }] },
    { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#388e3c" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#dce6f5" }] },
    { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#b8cef5" }] },
    { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#9ab8e8" }] },
    { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#12284A" }] },
    { featureType: "road.local", stylers: [{ visibility: "simplified" }] },
    { featureType: "transit", stylers: [{ visibility: "off" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#a8cce0" }] },
    { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3d6b8a" }] },
  ],
  disableDefaultUI: true,
  zoomControl: false,
};
</script>

<template>
  <div style="padding:0; position:relative; height:100%; width:100%; flex:1;">

    <div class="track-outer-panel">
      <!-- Skeleton in the panel's own footprint, instead of v-loading's
           full-screen grey mask. Arriving here from a placed order, that mask
           was a whole-viewport flash of grey between the confirmation and the
           tracking panel. A skeleton occupying the same box means the panel
           appears to fill in rather than the page appearing to reload. -->
      <div class="track-panel track-panel--skeleton" v-if="loading && !orderDetailsLoaded" aria-hidden="true">
        <div class="tsk__head">
          <span class="tsk__line tsk__line--sm"></span>
          <span class="tsk__line tsk__line--lg"></span>
        </div>
        <div class="tsk__body">
          <span class="tsk__line" style="width:62%"></span>
          <span class="tsk__line" style="width:84%"></span>
          <span class="tsk__line" style="width:48%"></span>
          <span class="tsk__rule"></span>
          <span class="tsk__line" style="width:72%"></span>
          <span class="tsk__line" style="width:56%"></span>
        </div>
      </div>
      <div class="track-panel" v-if="orderDetailsLoaded">

        <!-- Navy header -->
        <div class="track-panel-head">
          <div class="track-breadcrumb">
            <span class="track-breadcrumb-home" @click="router.push('/request')">Homepage</span>
            <span class="track-breadcrumb-sep">›</span>
            <span class="track-breadcrumb-current">Track Delivery</span>
          </div>

          <div class="track-order-title-row">
            <div class="track-order-number">{{ orderObject.data.order_details.order_number }}</div>
            <div
              class="track-status-badge"
              :style="{ background: statusColor.bg, color: statusColor.text }"
              :title="statusView.hint"
            >
              <span class="track-status-dot" :style="{ background: statusColor.dot }"></span>
              {{ statusView.label }}
            </div>
          </div>

          <div class="track-stops">
            <div
              v-for="(path, index) in orderObject.data.paths"
              :key="index"
              class="track-stop-row"
            >
              <div class="track-stop-icon-wrap">
                <div class="track-stop-dot" :class="Number(index) === 0 ? 'track-stop-dot--pickup' : 'track-stop-dot--drop'"></div>
                <div class="track-stop-line" v-if="Number(index) < orderObject.data.paths.length - 1"></div>
              </div>
              <div class="track-stop-label">
                <div class="track-stop-type">
                  {{ Number(index) === 0 ? 'Pick-up' : `Drop-off${orderObject.data.paths.length > 2 ? ' ' + index : ''}` }}
                </div>
                <div class="track-stop-name">{{ path.name }}</div>
                <template v-if="stopDetailAt(index)">
                  <div class="track-stop-contact">
                    <span v-if="stopDetailAt(index)!.contact_name">Contact {{ stopDetailAt(index)!.contact_name }}</span>
                    <span v-if="stopDetailAt(index)!.contact_phone"><a
                      :href="telHref(stopDetailAt(index)!.contact_phone)"
                      class="track-stop-tel"
                    >{{ formatPhone(stopDetailAt(index)!.contact_phone) }}</a></span>
                    <span v-if="stopDetailAt(index)!.reference">Ref {{ stopDetailAt(index)!.reference }}</span>
                  </div>
                  <div v-if="stopDetailAt(index)!.instructions" class="track-stop-instructions">
                    {{ stopDetailAt(index)!.instructions }}
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Body -->
        <div class="track-panel-body">

          <div class="track-section">
            <div class="track-section-title">Order Details</div>
            <div class="track-info-grid">
              <div class="track-info-row">
                <span class="track-info-label">Amount</span>
                <span class="track-info-value">KES {{ orderObject.data.payment_details.customer_amount.toLocaleString() }}</span>
              </div>
              <!-- Hold-until-approve note. The hint is on statusView but
                   only visible via native tooltip on desktop hover;
                   surface it explicitly under the amount so mobile /
                   touch customers see plainly that no charge has been
                   made yet. Only rendered for the two states where the
                   customer legitimately hasn't been charged. -->
              <div
                v-if="statusView.kind === 'pending_approval' || statusView.kind === 'docs_rejected'"
                class="track-info-row track-hold-note"
              >
                <span class="track-info-label">Payment</span>
                <span class="track-info-value track-hold-note__text">
                  You have not been charged yet. Payment will only be taken once Xobo Ops approves the delivery documents.
                </span>
              </div>
              <div
                v-else-if="statusView.kind === 'closed'"
                class="track-info-row track-hold-note"
              >
                <span class="track-info-label">Payment</span>
                <span class="track-info-value track-hold-note__text">
                  This order was closed by Xobo Ops — no charge will ever be made.
                </span>
              </div>
              <div v-for="r in extraInfoRows" :key="r.label" class="track-info-row">
                <span class="track-info-label">{{ r.label }}</span>
                <span class="track-info-value track-info-value--wrap">{{ r.value }}</span>
              </div>
              <div v-if="preferredNote" class="track-info-row">
                <span class="track-info-label">Driver</span>
                <span class="track-info-value track-info-value--wrap">{{ preferredNote }}</span>
              </div>
              <div v-else-if="awaitingDedicatedDriver" class="track-info-row">
                <span class="track-info-label">Driver</span>
                <span class="track-info-value track-info-value--wrap">{{ dedicatedDriverNote }}</span>
              </div>
              <div class="track-info-row">
                <span class="track-info-label">Placed on</span>
                <span class="track-info-value">{{ transformDate(orderObject.data.order_details.date_created) }}</span>
              </div>
              <div class="track-info-row" v-if="orderObject.data.order_details.order_status > 3 && droppedDeliveryLog">
                <span class="track-info-label">Delivered</span>
                <span class="track-info-value">
                  {{ transformDate(droppedDeliveryLog.log_time) }}
                </span>
              </div>
            </div>
          </div>

          <div class="track-section" v-if="orderObject.data.order_details.order_status != 1 && orderObject.data.driver_details">
            <div class="track-section-title">Driver</div>
            <div class="track-driver-card">
              <div class="track-driver-avatar">{{ orderObject.data.driver_details.name?.charAt(0)?.toUpperCase() }}</div>
              <div class="track-driver-info">
                <div class="track-driver-name">{{ orderObject.data.driver_details.name }}</div>
                <div class="track-driver-meta">{{ orderObject.data.driver_details.phone_number }}</div>
                <div class="track-driver-plate">{{ orderObject.data.driver_details.number_plate }}</div>
              </div>
            </div>
          </div>

          <div class="track-section">
            <div class="track-section-title">Activity</div>
            <div class="track-timeline">
              <div
                v-for="(activity, index) in customerDeliveryLogs"
                :key="index"
                class="track-timeline-item"
              >
                <div class="track-timeline-left">
                  <div class="track-timeline-dot" :class="Number(index) === 0 ? 'track-timeline-dot--active' : ''"></div>
                  <div class="track-timeline-connector" v-if="Number(index) < customerDeliveryLogs.length - 1"></div>
                </div>
                <div class="track-timeline-content">
                  <div class="track-timeline-event">{{ customerActivityLabel(activity) }}</div>
                  <div class="track-timeline-time">{{ transformDate(activity.log_time ?? '') }}</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Floating action buttons — bottom right -->
    <div class="track-fab-group">


<button
  class="track-fab track-fab--green" 
  @click="router.push('/request')"
  style="display: flex; align-items: center; justify-content: flex-start; gap: 8px; text-align: left;"
>
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
  New Order
</button>

<button
  class="track-fab track-fab--purple"
  @click="router.push('/transactions/orders')"
  style="display: flex; align-items: center; justify-content: flex-start; gap: 8px; text-align: left;"
>
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1 2 1-2-1Z" />
    <line x1="16" y1="8" x2="8" y2="8" />
    <line x1="16" y1="12" x2="8" y2="12" />
    <line x1="10" y1="16" x2="8" y2="16" />
  </svg>
  Order History
</button>

<button
  v-if="shareableToken"
  class="track-fab track-fab--orange"
  @click="shareTrackingLink"
  style="display: flex; align-items: center; justify-content: flex-start; gap: 8px; text-align: left;"
>
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
  Share Tracking Link
</button>


    </div>

    <!-- Share fallback menu (shown when navigator.share is unavailable) -->
    <div v-if="shareMenuOpen" class="share-overlay" @click.self="closeShareMenu">
      <div class="share-panel" role="dialog" aria-label="Share tracking link">
        <div class="share-panel-head">
          <span class="share-panel-title">Share tracking link</span>
          <button class="share-panel-close" @click="closeShareMenu" aria-label="Close">×</button>
        </div>
        <div class="share-panel-url" @click="copyShareUrl">{{ shareUrl }}</div>
        <div class="share-panel-options">
          <button class="share-opt" @click="copyShareUrl">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
            <span>Copy link</span>
          </button>
          <a class="share-opt" :href="whatsappShareHref" target="_blank" rel="noopener" @click="closeShareMenu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
              <path d="M20.5 3.5A11.9 11.9 0 0 0 12.05 0C5.46 0 .1 5.36.1 11.95c0 2.1.55 4.16 1.6 5.97L0 24l6.27-1.64a11.94 11.94 0 0 0 5.78 1.47h.01c6.59 0 11.95-5.36 11.95-11.95 0-3.2-1.24-6.2-3.51-8.38ZM12.06 21.8h-.01a9.93 9.93 0 0 1-5.06-1.39l-.36-.21-3.73.98 1-3.63-.24-.37a9.92 9.92 0 0 1-1.52-5.23c0-5.48 4.46-9.94 9.94-9.94 2.66 0 5.15 1.03 7.03 2.91a9.87 9.87 0 0 1 2.91 7.03c0 5.48-4.46 9.95-9.96 9.95Zm5.46-7.45c-.3-.15-1.77-.87-2.05-.97-.28-.1-.48-.15-.68.15-.2.3-.78.97-.95 1.17-.18.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.68-1.64-.93-2.24-.24-.59-.5-.51-.68-.52l-.58-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.06 2.87 1.21 3.07.15.2 2.09 3.2 5.07 4.49.71.31 1.27.49 1.7.63.71.23 1.36.2 1.87.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.18-1.42-.07-.13-.27-.2-.57-.35Z"/>
            </svg>
            <span>WhatsApp</span>
          </a>
          <a class="share-opt" :href="emailShareHref" @click="closeShareMenu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            <span>Email</span>
          </a>
          <a class="share-opt" :href="smsShareHref" @click="closeShareMenu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            <span>SMS</span>
          </a>
        </div>
      </div>
    </div>

    <!-- Share fallback menu (shown when navigator.share is unavailable) -->
    <div v-if="shareMenuOpen" class="share-overlay" @click.self="closeShareMenu">
      <div class="share-panel" role="dialog" aria-label="Share tracking link">
        <div class="share-panel-head">
          <span class="share-panel-title">Share tracking link</span>
          <button class="share-panel-close" @click="closeShareMenu" aria-label="Close">×</button>
        </div>
        <div class="share-panel-url" @click="copyShareUrl">{{ shareUrl }}</div>
        <div class="share-panel-options">
          <button class="share-opt" @click="copyShareUrl">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
            <span>Copy link</span>
          </button>
          <a class="share-opt" :href="whatsappShareHref" target="_blank" rel="noopener" @click="closeShareMenu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
              <path d="M20.5 3.5A11.9 11.9 0 0 0 12.05 0C5.46 0 .1 5.36.1 11.95c0 2.1.55 4.16 1.6 5.97L0 24l6.27-1.64a11.94 11.94 0 0 0 5.78 1.47h.01c6.59 0 11.95-5.36 11.95-11.95 0-3.2-1.24-6.2-3.51-8.38ZM12.06 21.8h-.01a9.93 9.93 0 0 1-5.06-1.39l-.36-.21-3.73.98 1-3.63-.24-.37a9.92 9.92 0 0 1-1.52-5.23c0-5.48 4.46-9.94 9.94-9.94 2.66 0 5.15 1.03 7.03 2.91a9.87 9.87 0 0 1 2.91 7.03c0 5.48-4.46 9.95-9.96 9.95Zm5.46-7.45c-.3-.15-1.77-.87-2.05-.97-.28-.1-.48-.15-.68.15-.2.3-.78.97-.95 1.17-.18.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.68-1.64-.93-2.24-.24-.59-.5-.51-.68-.52l-.58-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.06 2.87 1.21 3.07.15.2 2.09 3.2 5.07 4.49.71.31 1.27.49 1.7.63.71.23 1.36.2 1.87.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.18-1.42-.07-.13-.27-.2-.57-.35Z"/>
            </svg>
            <span>WhatsApp</span>
          </a>
          <a class="share-opt" :href="emailShareHref" @click="closeShareMenu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            <span>Email</span>
          </a>
          <a class="share-opt" :href="smsShareHref" @click="closeShareMenu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            <span>SMS</span>
          </a>
        </div>
      </div>
    </div>

    <GMapMap
      :center="mapCenter"
      :zoom="12"
      map-type-id="roadmap"
      :options="mapOptions"
      style="position:absolute; top:0; left:0; right:0; bottom:0; width:100%; height:100%;"
      ref="myMapRef"
    >
      <GMapMarker
        v-if="orderDetailsLoaded"
        :key="index"
        v-for="(m, index) in orderObject.data.paths"
        :position="m"
        :clickable="false"
        :draggable="false"
        :icon="getMarkerIcon(Number(index))"
      />
      <GMapPolyline
        v-if="routePath.length > 1"
        :path="routePath"
        :options="polylineOptions"
      />
    </GMapMap>

  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');

.track-outer-panel {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  /*max-width: 1280px; */
  margin: 0 auto;
  padding: 0 30px;
  pointer-events: none;
  z-index: 10;
}

.track-panel {
  position: absolute;
  top: 24px;
  left: 30px;
  width: 400px;
  max-height: calc(100vh - 110px);
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(18,40,74,0.16), 0 2px 8px rgba(18,40,74,0.08);
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  pointer-events: all;
  font-family: 'DM Sans', sans-serif;
}
.track-panel::-webkit-scrollbar { display: none; }

/* ── Loading skeleton ─────────────────────── */
.track-panel--skeleton { overflow: hidden; padding: 0; }
.tsk__head {
  background: linear-gradient(135deg, #12284A, #1e3f73);
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 9px;
}
.tsk__body { padding: 18px 20px; display: flex; flex-direction: column; gap: 11px; }
.tsk__line {
  display: block;
  height: 11px;
  width: 100%;
  border-radius: 6px;
  /* Contrast matters more than subtlety here. At #eef2f7/#e2e8f0 the bars
     were nearly invisible on the white panel, so the skeleton read as an
     empty card rather than as content arriving — which is the whole job. */
  background: linear-gradient(90deg, #e4eaf2 25%, #d3dced 37%, #e4eaf2 63%);
  background-size: 400% 100%;
  animation: tsk-shimmer 1.3s ease-in-out infinite;
}
.tsk__head .tsk__line {
  background: linear-gradient(90deg, rgba(255,255,255,.12) 25%, rgba(255,255,255,.22) 37%, rgba(255,255,255,.12) 63%);
  background-size: 400% 100%;
}
.tsk__line--sm { width: 38%; height: 9px; }
.tsk__line--lg { width: 68%; height: 15px; }
.tsk__rule { display: block; height: 1px; background: #dbe3ec; margin: 3px 0; }

@keyframes tsk-shimmer {
  0%   { background-position: 100% 0; }
  100% { background-position: 0 0; }
}
@media (prefers-reduced-motion: reduce) {
  .tsk__line { animation: none; }
}

/* ── Navy header ──────────────────────────── */
.track-panel-head {
  background: linear-gradient(135deg, #12284A 0%, #1e3f73 100%);
  padding: 20px 20px 18px;
  border-radius: 16px 16px 0 0;
}

.track-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;
}
.track-breadcrumb-home {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.5);
  cursor: pointer;
  transition: color 0.15s;
}
.track-breadcrumb-home:hover { color: rgba(255,255,255,0.85); }
.track-breadcrumb-sep { color: rgba(255,255,255,0.25); font-size: 0.75rem; }
.track-breadcrumb-current { font-size: 0.75rem; color: rgba(255,255,255,0.85); font-weight: 500; }

.track-order-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 8px;
}
.track-order-number {
  font-size: 1.05rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.03em;
}
.track-status-badge {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
}
.track-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.track-stops { display: flex; flex-direction: column; }
.track-stop-row { display: flex; gap: 10px; align-items: flex-start; }
.track-stop-icon-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding-top: 3px;
}
.track-stop-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.4);
  flex-shrink: 0;
}
.track-stop-dot--pickup { background: #22c55e; }
.track-stop-dot--drop { background: #f59e0b; }
.track-stop-line {
  width: 2px;
  height: 16px;
  background: rgba(255,255,255,0.15);
  margin: 3px 0;
}
.track-stop-label { flex: 1; padding-bottom: 8px; }
.track-stop-type {
  font-size: 0.67rem;
  color: rgba(255,255,255,0.4);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  margin-bottom: 1px;
}
.track-stop-name {
  font-size: 0.8rem;
  color: rgba(255,255,255,0.88);
  line-height: 1.3;
}
/* Receiver contact under a stop: "Contact Jane · 0722 000 111 · Ref INV-77". */
.track-stop-contact {
  margin-top: 3px;
  font-size: 0.74rem;
  line-height: 1.4;
  color: rgba(255,255,255,0.72);
}
.track-stop-contact > span + span::before { content: " · "; color: rgba(255,255,255,0.4); }
.track-stop-tel { color: #ffffff; text-decoration: underline; text-underline-offset: 2px; }
.track-stop-tel:focus-visible { outline: 2px solid rgba(255,255,255,0.8); outline-offset: 2px; border-radius: 2px; }
.track-stop-instructions {
  margin-top: 2px;
  font-size: 0.74rem;
  line-height: 1.4;
  font-style: italic;
  color: rgba(255,255,255,0.6);
  overflow-wrap: anywhere;
}

/* ── Body ─────────────────────────────────── */
.track-panel-body {
  padding: 14px 14px 22px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
}

.track-section {
  background: white;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  padding: 13px 15px;
  margin-bottom: 10px;
}
.track-section-title {
  font-size: 0.67rem;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 10px;
}

.track-info-grid { display: flex; flex-direction: column; gap: 8px; }
.track-info-row { display: flex; justify-content: space-between; align-items: flex-start; }
.track-info-label { font-size: 0.8rem; color: #9ca3af; font-weight: 500; }
.track-info-value {
  font-size: 0.82rem;
  color: #12284A;
  font-weight: 600;
  text-align: right;
  max-width: 60%;
}
.track-info-value--wrap { overflow-wrap: anywhere; }
.track-hold-note__text {
  font-weight: 500;
  color: #6d28d9;
  font-style: italic;
  line-height: 1.4;
}

.track-driver-card { display: flex; align-items: center; gap: 12px; }
.track-driver-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #12284A 0%, #1e3f73 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  flex-shrink: 0;
}
.track-driver-name { font-size: 0.88rem; font-weight: 600; color: #12284A; }
.track-driver-meta { font-size: 0.78rem; color: #6b7280; margin-top: 2px; }
.track-driver-plate {
  display: inline-block;
  background: #f3f4f6;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #374151;
  letter-spacing: 0.06em;
  margin-top: 5px;
}

.track-timeline { display: flex; flex-direction: column; }
.track-timeline-item { display: flex; gap: 10px; align-items: flex-start; }
.track-timeline-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding-top: 3px;
}
.track-timeline-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #d1d5db;
  box-shadow: 0 0 0 2px #f3f4f6;
  flex-shrink: 0;
}
.track-timeline-dot--active {
  background: #12284A;
  box-shadow: 0 0 0 2px rgba(18,40,74,0.2);
}
.track-timeline-connector {
  width: 2px;
  flex: 1;
  min-height: 16px;
  background: #e5e7eb;
  margin: 3px 0;
}
.track-timeline-content { flex: 1; padding-bottom: 14px; }
.track-timeline-event { font-size: 0.82rem; font-weight: 500; color: #374151; line-height: 1.3; }
.track-timeline-time { font-size: 0.72rem; color: #9ca3af; margin-top: 3px; }

/* ── Floating action buttons ──────────────── */
.track-fab-group {
  position: absolute;
  bottom: 100px;
  right: 32px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 10;
  pointer-events: all;
}
.track-fab {
  height: 44px;
  padding: 0 22px;
  border-radius: 22px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: all 0.18s cubic-bezier(0.4,0,0.2,1);
  letter-spacing: 0.02em;
  white-space: nowrap;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15), 0 1.5px 4px rgba(0,0,0,0.08);
  width:200px;
}
.track-fab:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.2), 0 2px 6px rgba(0,0,0,0.1);
}
.track-fab:active { transform: translateY(0); }
.track-fab--green {
  background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
  color: white;
}
.track-fab--purple {
  background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
  color: white;
}
.track-fab--orange {
  background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%);
  color: white;
}

/* ── Share fallback popover ───────────────────────────── */
.share-overlay {
  position: fixed;
  inset: 0;
  background: rgba(18,40,74,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  pointer-events: all;
  font-family: 'DM Sans', sans-serif;
}
.share-panel {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(18,40,74,0.25);
  width: min(380px, calc(100vw - 32px));
  padding: 18px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.share-panel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.share-panel-title {
  font-size: 1rem;
  font-weight: 600;
  color: #12284A;
}
.share-panel-close {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: #6b7280;
  cursor: pointer;
  padding: 0 4px;
}
.share-panel-close:hover { color: #12284A; }
.share-panel-url {
  background: rgba(18,40,74,0.05);
  border: 1px solid rgba(18,40,74,0.08);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.8rem;
  color: #12284A;
  word-break: break-all;
  cursor: pointer;
  user-select: all;
}
.share-panel-url:hover { background: rgba(18,40,74,0.08); }
.share-panel-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.share-opt {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: rgba(18,40,74,0.04);
  border: 1px solid rgba(18,40,74,0.08);
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #12284A;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s, transform 0.05s;
  font-family: inherit;
}
.share-opt:hover {
  background: rgba(18,40,74,0.08);
}
.share-opt:active { transform: scale(0.98); }

/* Phones: the 400 px panel spans the screen with a 12 px gutter and stops
   above the action buttons, which move to the bottom corner. */
@media (max-width: 480px) {
  .track-outer-panel { padding: 0; }
  .track-panel {
    left: 12px;
    right: 12px;
    top: 12px;
    width: auto;
    max-height: calc(100vh - 250px);
  }
  .track-fab-group { bottom: 16px; right: 16px; gap: 8px; }
  .track-fab { width: auto; height: 40px; padding: 0 16px; }
}
</style>